import {
  AdminInputPlaceholder,
  Button,
  Div,
  LocationVDriveEntity,
  Modal,
  P,
  Separator,
  UserRole,
  VDrivePaymentMode,
  VDrivePaymentModeOptions,
  updateVDrivePaymentMode,
  useAuth,
  useBusyAction,
  useVDrive,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import {
  AutoRechargeComponent,
  AutoRechargeWhenComponent,
  autoRechargeAmounts,
} from './onboarding/SetAutoRecharge';

const TOOLTIP =
  'Per delivery: no money up front, each delivery charges the card only for what the balance is missing (at least $1.00), and credit on the balance is spent first. Top-up: the card adds the auto recharge amount whenever the balance drops below the set amount, with lower card fees. Applies to every location sharing this balance.';

const MODE_LABEL: Record<VDrivePaymentMode, string> = {
  [VDrivePaymentMode.perDelivery]: 'Per delivery',
  [VDrivePaymentMode.topUp]: 'Top-up',
};

type Props = {
  vDrive: LocationVDriveEntity;
  /** The page keeps its own auto-recharge amounts, sent back with Save. */
  onSaved: (amounts: {
    autoRechargeAmount: number;
    autoRechargeWhenAmount: number;
  }) => void;
};

/**
 * How the balance is paid for, and the admin and support way to change it.
 * Saved on its own route like the reserve limit, not with the page's Save.
 */
export const PaymentModeField = ({ vDrive, onSaved }: Props) => {
  const role = useAuth((state) => state.user?.role);
  const canEdit = role === UserRole.admin || role === UserRole.support;
  const [open, setOpen] = useState(false);

  // `null` without a balance, undefined from a backend before the change
  // (top-up only then).
  const mode = vDrive.paymentMode;

  return (
    <>
      <AdminInputPlaceholder
        label="Payment"
        tooltip={TOOLTIP}
        $textCenter
        value={mode ? MODE_LABEL[mode] : 'Top-up'}
        onClick={canEdit && mode !== null ? () => setOpen(true) : undefined}
      />
      <PaymentModeModal
        isOpen={open}
        onClose={() => setOpen(false)}
        vDrive={vDrive}
        onSaved={onSaved}
      />
    </>
  );
};

type ModalProps = Props & {
  isOpen: boolean;
  onClose: () => void;
};

const PaymentModeModal = ({ isOpen, onClose, vDrive, onSaved }: ModalProps) => {
  const setLocationVDrive = useVDrive((state) => state.setLocationVDrive);
  const [mode, setMode] = useState(VDrivePaymentMode.perDelivery);
  const [amount, setAmount] = useState(autoRechargeAmounts[1].value);
  const [whenAmount, setWhenAmount] = useState<number | null>(
    autoRechargeAmounts[0].value
  );
  const { busy, run } = useBusyAction();

  // Every opening starts from what is saved, not from an abandoned edit.
  useEffect(() => {
    if (!isOpen) return;
    setMode(vDrive.paymentMode ?? VDrivePaymentMode.topUp);
    setAmount(vDrive.autoRechargeAmount || autoRechargeAmounts[1].value);
    setWhenAmount(
      vDrive.autoRechargeWhenAmount || autoRechargeAmounts[0].value
    );
  }, [
    isOpen,
    vDrive.paymentMode,
    vDrive.autoRechargeAmount,
    vDrive.autoRechargeWhenAmount,
  ]);

  const topUp = mode === VDrivePaymentMode.topUp;

  const save = async () => {
    const ok = await run(async () => {
      const { data } = await updateVDrivePaymentMode(
        vDrive.location.id,
        // Top-up always with both amounts: with none saved it is a 400.
        topUp
          ? {
              paymentMode: mode,
              autoRechargeAmount: Number(amount),
              autoRechargeWhenAmount: Number(whenAmount),
            }
          : { paymentMode: mode }
      );
      const saved = {
        autoRechargeAmount: data.data.autoRechargeAmount ?? Number(amount),
        autoRechargeWhenAmount:
          data.data.autoRechargeWhenAmount ?? Number(whenAmount),
      };
      // Only these fields: the page keeps its own filtered copy of the rest
      // (providers without `unknown`, unsaved assignment edits).
      setLocationVDrive({
        ...vDrive,
        ...saved,
        paymentMode: data.data.paymentMode,
      });
      onSaved(saved);
    }, "You've successfully updated how this location pays for vDrive.");

    if (ok) onClose();
  };

  return (
    <Modal isOpen={isOpen} closeModal={onClose} logo={false} header="Payment">
      <P $mb="24" $center>
        How this vDrive balance is paid for. The change applies to every
        location sharing the balance; a positive balance stays and is spent on
        the next deliveries.
      </P>

      <VDrivePaymentModeOptions
        value={mode}
        onChange={setMode}
        topUpAmount={Number(amount)}
      />

      {topUp && (
        <Div $flex $column $gap="4px" $mt="20">
          <AutoRechargeComponent
            autoRechargeAmount={amount}
            setAutoRechargeAmount={(value) => setAmount(Number(value))}
          />
          <Separator $m="4px 0" />
          <AutoRechargeWhenComponent
            autoRechargeWhenAmount={whenAmount}
            setAutoRechargeWhenAmount={setWhenAmount}
          />
        </Div>
      )}

      <Button
        $selected
        $w="100%"
        $mt="24"
        $loading={busy}
        disabled={busy}
        onClick={save}
      >
        Save
      </Button>
      <Button $selected $gray $w="100%" $mt="16" onClick={onClose}>
        Cancel
      </Button>
    </Modal>
  );
};
